import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, RotateCcw, X, icons } from 'lucide-react';
import { MoodActivity, MoodType } from '@/data/moodActivities';
import { cn } from '@/lib/utils';

interface ActivitySuggestionProps {
  activity: MoodActivity;
  mood: MoodType;
  onDismiss: () => void;
  onReset: () => void;
}

const moodGradients: Record<MoodType, string> = {
  happy: 'from-teal-50 to-white',
  sad: 'from-purple-50 to-white',
  annoyed: 'from-blue-50 to-white',
  relaxed: 'from-teal-50 to-white',
};

const moodAccent: Record<MoodType, string> = {
  happy: 'bg-teal-600 hover:bg-teal-700 text-white',
  sad: 'bg-purple-600 hover:bg-purple-700 text-white',
  annoyed: 'bg-blue-600 hover:bg-blue-700 text-white',
  relaxed: 'bg-teal-600 hover:bg-teal-700 text-white',
};

const moodIconColor: Record<MoodType, string> = {
  happy: 'text-teal-600',
  sad: 'text-purple-600',
  annoyed: 'text-blue-600',
  relaxed: 'text-teal-600',
};

const moodIconBg: Record<MoodType, string> = {
  happy: 'bg-teal-100',
  sad: 'bg-purple-100',
  annoyed: 'bg-blue-100',
  relaxed: 'bg-teal-100',
};

const ActivitySuggestion: React.FC<ActivitySuggestionProps> = ({
  activity,
  mood,
  onDismiss,
  onReset,
}) => {
  const ActivityIcon = icons[activity.iconName as keyof typeof icons];

  return (
    <Card className={cn('border shadow-md overflow-hidden bg-gradient-to-br', moodGradients[mood])}>
      <CardContent className="p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-start gap-5">
          {/* Activity icon */}
          <div className={cn('flex items-center justify-center w-16 h-16 rounded-full flex-shrink-0 self-center md:self-start', moodIconBg[mood])}>
            {ActivityIcon && <ActivityIcon className={cn('h-8 w-8', moodIconColor[mood])} />}
          </div>

          {/* Content */}
          <div className="flex-1 text-center md:text-left">
            <h4 className="font-bold text-lg text-gray-900 mb-1">
              {activity.title}
            </h4>
            <div className="flex items-center justify-center md:justify-start gap-1.5 text-gray-500 text-xs mb-3">
              <Clock className="h-3.5 w-3.5" />
              <span>{activity.duration}</span>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed mb-5">
              {activity.description}
            </p>

            <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
              <Button size="sm" className={cn(moodAccent[mood])}>
                Try This
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onReset}
                className="gap-1.5"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Change Mood
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onDismiss}
                className="gap-1.5 text-gray-500"
              >
                <X className="h-3.5 w-3.5" />
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ActivitySuggestion;
